/*
 * Copyright (c) 2014-2023 Inovalon, Inc. All Rights Reserved.
 * This computer program is CONFIDENTIAL and a TRADE SECRET of Inovalon, Inc.
 * The receipt or possession of this program does not convey any rights to use, reproduce or disclose
 * its contents in whole or in part, without the specific written consent of Inovalon, Inc.
 * Any use, reproduction or disclosure of this program without the express written consent of Inovalon, Inc.
 * is a violation of the copyright laws and may subject you to criminal prosecution.
 */
import axios from 'axios'
import axiosRetry from 'axios-retry'
import { injectable } from 'inversify'
import { IBatchesApiClient } from './contracts'
import { injectProperty } from '../../arc/configuration/ServiceLocator'
import { IUserSessionMiddleware } from '../../arc/middleware/UserSessionMiddleware/contracts'
import { SettingsManager } from '../../configuration/SettingsManager'
import { ErrorHelper } from '../../utils/ErrorHelper'
import { ICoverageDiscoveryBatchHistoryGridResults, ICoverageDiscoveryBatchHistoryGridCriteria, IOwnerUser } from '../BatchesStateManager/contracts'

@injectable()
export class BatchesApiClient implements IBatchesApiClient {
	@injectProperty('IUserSessionMiddleware')
	private readonly userSessionMiddleware!: IUserSessionMiddleware

	list(criteria: ICoverageDiscoveryBatchHistoryGridCriteria): Promise<ICoverageDiscoveryBatchHistoryGridResults> {
		let filterString = ''

		if (criteria.searchTerm.runDate) {
			filterString += `&runDate=${criteria.searchTerm.runDate.toDateString()}`
			filterString += `&duration=${criteria.dateRange}`
		}

		if (criteria.searchTerm.transactionStatuses) {
			if (criteria.searchTerm.transactionStatuses.length !== 0) {
				const ids = criteria.searchTerm.transactionStatuses.map(item => item.id)
				filterString += `&transactionStatuses=${ids}`
			}
		}

		if (criteria.searchTerm.ownerUsers) {
			if (criteria.searchTerm.ownerUsers.length !== 0) {
				const ids = criteria.searchTerm.ownerUsers.map(item => item.id)
				filterString += `&ownerUsers=${ids}`
			}
		}

		const dateTimeStamp = `t=${Date.now()}`
		const sortField = criteria.sortField.charAt(0).toUpperCase() + criteria.sortField.slice(1)
		const sort = `${criteria.sortOrder === 'asc' ? '%2b' : '-'}${sortField}`
		criteria.page = criteria.page > 0 ? criteria.page : 1
		const offset = (criteria.page - 1) * criteria.sizePerPage
		const limit = criteria.sizePerPage
		const params = `Sort=${sort}&Offset=${offset}&Limit=${limit}${filterString}&${dateTimeStamp}`
		const url = `${SettingsManager.REACT_APP_MAIN_API_BASE_URL().value}/batches/getBatchesForHistory?${params}`

		return this.userSessionMiddleware
			.loadSessionJwt()
			.then(token => {
				axiosRetry(axios, {
					retries: 1,
					retryCondition: (error: any) => error && error.response && error.response.status === 401,
				})
				return axios.get(url, {
					headers: {
						Authorization: `Bearer ${token}`,
					},
				})
			})
			.then(response => response.data)
			.catch(error => {
				ErrorHelper.handleApiError(error)
			})
	}

	deleteSelectedRecord(batchId: number, batchRunId: number): Promise<void> {
		const dateTimeStamp = `t=${Date.now()}`
		const params = `batchId=${batchId}&batchRunId=${batchRunId}&${dateTimeStamp}`
		const url = `${SettingsManager.REACT_APP_MAIN_API_BASE_URL().value}/batches/deleteBatchRecord?${params}`
		return this.userSessionMiddleware
			.loadSessionJwt()
			.then(token =>
				axios.delete(url, {
					headers: {
						Authorization: `Bearer ${token}`,
					},
				}),
			)
			.then(response => response.data)

			.catch(error => {
				ErrorHelper.handleApiError(error)
			})
	}

	getAllBatchOwners(): Promise<IOwnerUser[]> {
		const url = `${SettingsManager.REACT_APP_MAIN_API_BASE_URL().value}/batches/getBatchOwnersForFilter`
		return this.userSessionMiddleware.loadSessionJwt().then(token =>
			axios
				.get(url, {
					headers: {
						Authorization: `Bearer ${token}`,
					},
				})
				.then(response => response.data.items)
				.catch(error => {
					ErrorHelper.handleApiError(error)
				}),
		)
	}
}
